// src/pages/portal/Catalog.tsx
//
// Portal catalog — M2-02: live products from /api/products (real
// FileMaker catalog) instead of demoData. Tier is pinned to "standard"
// (list price) until the business pricing policy lands.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShoppingBag, Plus, Check, RefreshCcw } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { type Product, type Category } from "@/lib/demoData";
import { usePortal } from "@/lib/portal/PortalContext";
import { useCart } from "@/lib/portal/cartStore";
import { calcNetPrice } from "@/lib/pricing";
import { formatPrice } from "@/lib/formatPrice";
import { fetchProducts, mapApiProduct } from "@/lib/api";
import { cn } from "@/lib/cn";

const CATEGORIES: Category[] = ["video", "audio", "screens", "acoustic", "accessories"];
const PAGE_SIZE = 60;

/** Display copy for the category filter buttons (the machine value stays the slug). */
const CATEGORY_LABEL_KEY: Record<Category | "all", string> = {
  all: "portal.catalog.categoryAll",
  video: "portal.catalog.categoryVideo",
  audio: "portal.catalog.categoryAudio",
  screens: "portal.catalog.categoryScreens",
  acoustic: "portal.catalog.categoryAcoustic",
  accessories: "portal.catalog.categoryAccessories",
};

export function PortalCatalog() {
  const { t } = useTranslation();
  const { customer } = usePortal();
  const cart = useCart();
  const [category, setCategory] = useState<Category | "all">("all");
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    const id = setTimeout(() => {
      fetchProducts({
        q: query || undefined,
        category: category === "all" ? undefined : category,
        limit: PAGE_SIZE,
      })
        .then((page) => {
          if (cancelled) return;
          setProducts(page.rows.map(mapApiProduct));
          setTotal(page.total);
        })
        .catch((e) => {
          if (!cancelled) setError(String(e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [query, category]);

  if (!customer) return null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-510 tracking-tight text-fg-primary">{t("portal.catalog.title")}</h2>
        <div className="text-xs text-fg-tertiary">
          {t("portal.catalog.productsAtListPrice", { n: products.length, total: total.toLocaleString() })}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("portal.catalog.searchPlaceholder")}
          className="w-64"
        />
        <div className="flex flex-wrap gap-1">
          {(["all", ...CATEGORIES] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
              className={cn(
                "rounded px-2 py-1 text-xs font-510 uppercase tracking-wider transition-colors",
                category === c
                  ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                  : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
              )}
            >
              {t(CATEGORY_LABEL_KEY[c])}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          {t("portal.catalog.apiError")} ({error})
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
          {t("portal.catalog.loadingProducts")}
        </p>
      ) : products.length === 0 && !error ? (
        <EmptyState
          title={t("portal.catalog.empty")}
          description={t("portal.catalog.emptyHint")} 
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {products.map((p) => (
            <PortalProductCard key={p.sku} product={p} customer={customer} cart={cart} />
          ))}
        </div>
      )}
    </div>
  );
}

interface PortalProductCardProps {
  product: Product;
  customer: NonNullable<ReturnType<typeof usePortal>["customer"]>;
  cart: ReturnType<typeof useCart>;
}
function PortalProductCard({ product, customer, cart }: PortalProductCardProps) {
  const { t } = useTranslation();
  const tier = customer.pricingTier;
  const price = calcNetPrice({ listPrice: product.priceEUR, tier, qty: 1 });
  const inCart = cart.items.find((it) => it.sku === product.sku);

  return (
    <div className="rounded-lg border border-border bg-bg-surface">
      <div className="aspect-[16/9] w-full overflow-hidden rounded-t-lg bg-bg-elevated">
        {product.image && product.image !== "x" ? (
          <img src={product.image} alt={product.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-fg-quaternary">
            <ShoppingBag className="h-10 w-10" strokeWidth={1} />
          </div>
        )}
      </div>
      <div className="space-y-2 p-4">
        <div className="flex items-baseline justify-between gap-2">
          <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] text-fg-secondary">
            {product.brand}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
            {product.category}
          </span>
          <code className="ml-auto font-mono text-[10px] text-fg-quaternary">{product.sku}</code>
        </div>
        <div className="text-sm font-510 tracking-tight text-fg-primary">
          {product.name}
        </div>
        <div className="border-t border-border pt-2">
          {price.isQuoteOnly ? (
            <div className="text-base font-510 text-fg-quaternary">— — —</div>
          ) : (
            <>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-lg font-510 text-fg-primary">
                  {formatPrice(price.unitPrice, "EUR")}
                </span>
                {price.discount > 0 && (
                  <span className="text-xs text-fg-quaternary line-through">
                    {formatPrice(product.priceEUR, "EUR")}
                  </span>
                )}
              </div>
              {price.discount > 0 && (
                <Badge variant="teal" size="sm">
                  {t("portal.catalog.youSave", { discount: price.discountLabel })}
                </Badge>
              )}
            </>
          )}
        </div>
        <Button
          size="sm"
          className="w-full"
          disabled={price.isQuoteOnly}
          onClick={() =>
            cart.addItem({
              sku: product.sku,
              name: product.name,
              brand: product.brand,
              image: product.image,
              listPriceEUR: product.priceEUR,
            })
          }
        >
          {inCart ? (
            <>
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("portal.catalog.added", { qty: inCart.qty })}
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
              {t("portal.catalog.addToCart")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
